/**
 * Component for displaying a row in an info table with icons.
 * Each row contains a title with an icon and information that can be displayed in various formats:
 * - As a single string
 * - As an array of strings (bullet points)
 * - As an array of social media links with icons
 * - As an image
 *
 * @param {IGeneralInfoData} data - The data object containing id, title, icon source, and info
 * @param {boolean} [isFirst] - Whether this is the first row in the table
 * @param {boolean} [showBullets] - Whether to show bullets for list items
 * @param {number} [titleTextWeight=500] - Font weight for the title text
 */

import { Image, Link, StyleSheet, Text, View } from '@react-pdf/renderer';
import { getColorObjectStyle, getColorObjectStyleByRecord } from 'PDF/helpers/documentStyle';
import { IInfoImg } from 'PDF/helpers/pdfMapping';

import { List } from '../List/List';

export interface IInfoItem {
  iconSrc: string;
  link: string;
}

type Info = string | string[] | IInfoItem[] | IInfoImg;

export interface IGeneralInfoData {
  id: string | number;
  title: string;
  iconSrc?: string;
  info: Info;
}

interface IInfoTableRowProps {
  data: IGeneralInfoData;
  isFirst?: boolean;
  showBullets?: boolean;
  titleTextWeight?: number;
}

const isIcons = (info: Info): info is IInfoItem[] =>
  Array.isArray(info) && typeof info[0] === 'object' && !!info[0].iconSrc;
const isImg = (info: Info): info is IInfoImg => !Array.isArray(info) && typeof info === 'object' && !!info.imgSrc;
const isArray = (info: Info) => Array.isArray(info);
const isString = (info: Info): info is string => typeof info === 'string';

const colorIndexesDot = { green: 12, blue: 0, red: 0, blueGreen: 0, blueRed: 1, blueYellow: 0 };

export const InfoTableRow = ({
  data: { id, info, title, iconSrc },
  isFirst = false,
  showBullets,
  titleTextWeight = 500,
}: IInfoTableRowProps) => {
  return (
    <View style={styles.rowWrapper} wrap={false} key={id}>
      <View
        style={[
          { padding: isArray(info) && !isIcons(info) ? '6.5px 10px' : '5.50px 10px' },
          styles.titleWrapper,
          getColorObjectStyle(2, 'backgroundColor'),
        ]}
      >
        <Image src={iconSrc} style={[styles.icon, getColorObjectStyle(2, 'backgroundColor')]} />
        <Text style={[styles.titleText, { fontWeight: titleTextWeight }]}>{title}</Text>
      </View>
      <View
        style={[
          {
            borderTopColor: isFirst ? '#77787C' : '#fff',
            padding: '5.5px 0 5.5px',
          },
          styles.infoWrap,
        ]}
      >
        {isImg(info) && <Image src={info.imgSrc} style={styles.img} />}
        {isIcons(info) && (
          <View style={styles.socialsWrap}>
            {info.map(
              (icon) =>
                typeof icon === 'object' &&
                'iconSrc' in icon && (
                  <View style={styles.iconWrap} key={icon.iconSrc}>
                    <Image style={styles.socialIcon} src={icon.iconSrc} />
                    <Link src={icon.link} key={icon.iconSrc} style={{ ...styles.infoText, ...styles.infoTextWithIcon }}>
                      {icon.link}
                    </Link>
                  </View>
                ),
            )}
          </View>
        )}
        {isArray(info) && !isIcons(info) && (
          <List
            steps={info as string[]}
            color={getColorObjectStyleByRecord(colorIndexesDot, 'color')}
            noPadding
            showBullets={showBullets}
          />
        )}
        {isString(info) && (
          <Text style={styles.infoText}>
            {info}
          </Text>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  rowWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    marginTop: -1,
  },
  titleWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    height: '100%',
    width: '59%',
  },
  titleText: {
    color: '#212121',
    fontFamily: 'Noto',
    fontWeight: 500,
    fontSize: 9,
    lineHeight: 1.36,
    marginHorizontal: '8px',
  },
  infoWrap: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    borderBottomStyle: 'dotted',
    borderBottomWidth: 0.5,
    borderTopStyle: 'dotted',
    borderTopWidth: 0.5,
    marginLeft: '16px',
    borderBottomColor: '#77787C',
  },
  infoText: {
    color: '#000000',
    fontFamily: 'Noto',
    fontWeight: 300,
    fontSize: 9,
    lineHeight: 1.36,
    textDecoration: 'none',
  },
  infoTextWithIcon: {
    maxWidth: '96%',
  },
  icon: {
    width: '12px',
    height: '12px',
  },
  socialIcon: {
    marginTop: 4,
    width: 10,
    height: 10,
    marginRight: 2,
  },
  socialsWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  iconWrap: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  img: {
    height: '103px',
    objectFit: 'contain',
    objectPositionX: 0,
    objectPositionY: 0,
  },
});
